import { effectiveAnnualUsageKwhForPricing } from '../../helpers/utilityMonthlyDailyToAnnual';
import designServices from '../../services/oneButtonServices/designServices';
import opportunityInfoService from '../../services/oneButtonServices/opportunityInfoService';
import type { PreContractIntakeForm } from './preContractTypes';
import { UTILITY_OPTIONS } from './preContractTypes';

export type PreContractUtilityPrefill = {
    utilityCompany: string;
    annualUsageKwh: string;
    utilityRate: string;
};

function normalizeCompanyName(name: string): string {
    return name.trim().toLowerCase().replace(/\s+/g, ' ');
}

/** Map opportunity utility name to intake select options when possible. */
export function resolveUtilityCompanyLabel(rawName: string | null | undefined): string {
    const name = String(rawName ?? '').trim();
    if (!name) return '';

    const normalized = normalizeCompanyName(name);
    const exact = UTILITY_OPTIONS.find((option) => normalizeCompanyName(option) === normalized);
    if (exact) return exact;

    const partial = UTILITY_OPTIONS.find((option) => {
        const optionNorm = normalizeCompanyName(option);
        return normalized.includes(optionNorm) || optionNorm.includes(normalized);
    });
    if (partial) return partial;

    if (normalized.includes('united illuminating') || normalized === 'ui') return 'United Illuminating';
    if (normalized.includes('cl&p') || normalized.includes('clp')) return 'Eversource (CL&P)';
    if (normalized.includes('national grid')) return 'National Grid';
    if (normalized.includes('con ed') || normalized.includes('coned')) return 'Con Edison';
    if (normalized.includes('eversource')) return 'Eversource';

    return name;
}

export function utilityCompanySelectOptions(current: string): string[] {
    const trimmed = current.trim();
    if (!trimmed || UTILITY_OPTIONS.includes(trimmed)) return [...UTILITY_OPTIONS];
    return [trimmed, ...UTILITY_OPTIONS];
}

function formatRate(value: unknown): string {
    const num = Number(value);
    if (!Number.isFinite(num) || num <= 0) return '';
    return String(num);
}

function formatAnnualUsage(value: number | null): string {
    if (value == null || !Number.isFinite(value) || value <= 0) return '';
    return String(Math.round(value));
}

export function mergeUtilityPrefill(
    form: PreContractIntakeForm,
    prefill: PreContractUtilityPrefill,
): Partial<PreContractIntakeForm> {
    const patch: Partial<PreContractIntakeForm> = {};
    if (!form.utilityCompany.trim() && prefill.utilityCompany) {
        patch.utilityCompany = prefill.utilityCompany;
    }
    if (!form.annualUsageKwh.trim() && prefill.annualUsageKwh) {
        patch.annualUsageKwh = prefill.annualUsageKwh;
    }
    if (!form.utilityRate.trim() && prefill.utilityRate) {
        patch.utilityRate = prefill.utilityRate;
    }
    return patch;
}

export function hasUtilityPrefill(prefill: PreContractUtilityPrefill | null): boolean {
    if (!prefill) return false;
    return !!(prefill.utilityCompany || prefill.annualUsageKwh || prefill.utilityRate);
}

export async function fetchUtilityPrefillFromOpportunity(
    salesForceId: string,
): Promise<PreContractUtilityPrefill | null> {
    const sfid = salesForceId.trim();
    if (!sfid) return null;

    try {
        const detailRaw = await opportunityInfoService.getOpportunityDetail(sfid);
        const opportunity = detailRaw != null ? (detailRaw?.data ?? detailRaw) : null;
        const opportunityId = opportunity?.Opportunity_Id;
        const opportunityIdStr =
            opportunityId != null && String(opportunityId).trim() !== '' ? String(opportunityId) : null;

        if (!opportunityIdStr) return null;

        const utilityResult = await designServices.RetrieveUtilityInformation(opportunityIdStr).catch(() => null);
        const solarUtility = (utilityResult?.utilityDetails?.SolarUtility ?? {}) as Record<string, unknown>;

        const utilityCompany = resolveUtilityCompanyLabel(
            String(solarUtility.UtilityCompanyName ?? solarUtility.utilityCompanyName ?? ''),
        );
        const annualUsageKwh = formatAnnualUsage(effectiveAnnualUsageKwhForPricing(solarUtility, null));
        const utilityRate = formatRate(
            solarUtility.CurrentAllInUtilityRate ?? solarUtility.Utility_Rate ?? solarUtility.utilityRate,
        );

        if (!utilityCompany && !annualUsageKwh && !utilityRate) return null;

        return { utilityCompany, annualUsageKwh, utilityRate };
    } catch {
        return null;
    }
}
